import React, { useState, useEffect } from "react";
import "./styles/adult.css";

// Robust DOB parser accepting ISO and common US formats
function parseDOBtoDate(dobStr) {
  if (!dobStr) return null;
  const s = String(dobStr).trim();
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) {
    const [, mm, dd, yyyy] = m;
    const dt = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    if (!Number.isNaN(dt.getTime())) return dt;
  }
  return null;
}

  function calculateAge(dobStr) {
    if (!dobStr) return null;
    const dobDate = parseDOBtoDate(dobStr);
    if (!dobDate) return null;
    const now = new Date();
    let age = now.getFullYear() - dobDate.getFullYear();
    const m = now.getMonth() - dobDate.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < dobDate.getDate())) age--;
    return Number.isFinite(age) ? age : null;
  }

  const SUBMIT_URL = "/.netlify/functions/append-sheet";
  const REDIRECT_TO = "/policy";

  export default function CollegeForm({
    submitUrl = SUBMIT_URL,
    redirectTo = REDIRECT_TO,
    onComplete
  }) {
    // Personal info
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [dob, setDob] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");

    // Honeypot (should remain empty)
    const [website, setWebsite] = useState("");

    // Stay / rooming
    // options: 'girls-cabin' | 'boys-cabin' | 'dorms' | 'married'
    const [stayOption, setStayOption] = useState("girls-cabin");
    // dormChoice will be a radio: "Girls dorm" | "Boys dorm"
    const [dormChoice, setDormChoice] = useState("");
    const [spouseName, setSpouseName] = useState("");

    // Handicap (moved up under room options) - boolean (false = no, true = yes)
    const [handicapAccessible, setHandicapAccessible] = useState(false);

    // Emergency contacts
    const [emergency1Name, setEmergency1Name] = useState("");
    const [emergency1Phone, setEmergency1Phone] = useState("");
    const [emergency2Name, setEmergency2Name] = useState("");
    const [emergency2Phone, setEmergency2Phone] = useState("");

    // Allergies
    // NOTE: keep as "yes"/"no" because validate() + payload expect that.
    const [hasAllergies, setHasAllergies] = useState("no"); // 'yes'|'no'
    const [allergies, setAllergies] = useState("");
    const [allergyAcknowledged, setAllergyAcknowledged] = useState(false);

    // UI state (FIXED: submitting state added back)
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    function formatDobMMDDYYYY(input) {
      const digits = String(input || "").replace(/\D/g, "").slice(0, 8);
      const mm = digits.slice(0, 2);
      const dd = digits.slice(2, 4);
      const yyyy = digits.slice(4, 8);
      if (digits.length <= 2) return mm;
      if (digits.length <= 4) return `${mm}/${dd}`;
      return `${mm}/${dd}/${yyyy}`;
    }

    function handleDobChange(e) {
      setDob(formatDobMMDDYYYY(e.target.value));
    }

    // compute age for validation only (no visible age input)
    useEffect(() => {
      calculateAge(dob);
    }, [dob]);

    const validate = () => {
      // Required checks (everything required except emergency2Name/Phone)
      if (!firstName.trim()) return "First name is required.";
      if (!lastName.trim()) return "Last name is required.";
      if (!dob) return "Date of birth is required.";
      const numericAge = calculateAge(dob);
      if (numericAge === null || Number.isNaN(numericAge)) return "Please provide a valid DOB.";
      if (numericAge < 18 || numericAge > 23) return "College form is for ages 18–23. Please use the correct form.";
      if (!phone.trim()) return "Phone is required.";
      if (!email.trim()) return "Email is required.";
      if (!stayOption) return "Please indicate where you will be staying.";
      // If user chose Dorms we require a dorm radio selection (girls/boys dorm)
      if (stayOption === "dorms" && !dormChoice.trim()) return "Please choose Girls dorm or Boys dorm.";
      if (stayOption === "married" && !spouseName.trim()) return "Please provide spouse name.";
      // Emergency contact 1 required
      if (!emergency1Name.trim()) return "Emergency contact 1 name is required.";
      if (!emergency1Phone.trim()) return "Emergency contact 1 phone is required.";
      // Allergies: if yes require details and acknowledgement
      if (hasAllergies === "yes") {
        if (!allergies.trim()) return "Please list allergies.";
        if (!allergyAcknowledged) return "Please acknowledge the allergy policy.";
      }
      return null;
    };

    const buildPayload = () => ({
      formType: "college",
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      dob: dob || undefined,
      age: (() => {
        const a = calculateAge(dob);
        return a !== null ? String(a) : undefined;
      })(),
      phone: phone.trim(),
      email: email.trim(),

      // honeypot (keep in payload so the server can also reject bots)
      website: website.trim() || undefined,

      stayOption,
      // dormChoice will be "Girls dorm" or "Boys dorm" when applicable
      dormChoice: dormChoice.trim() || undefined,
      spouseName: spouseName.trim() || undefined,
      handicapAccessible: !!handicapAccessible,
      emergency1Name: emergency1Name.trim() || undefined,
      emergency1Phone: emergency1Phone.trim() || undefined,
      emergency2Name: emergency2Name.trim() || undefined,
      emergency2Phone: emergency2Phone.trim() || undefined,
      hasAllergies,
      allergies: hasAllergies === "yes" ? allergies.trim() : undefined,
      allergyAcknowledged: !!allergyAcknowledged
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      setError("");

      // Honeypot: bots fill this, humans won't
      if (String(website || "").trim()) {
        // Fail silently to avoid giving bots feedback
        return;
      }

      const v = validate();
      if (v) {
        setError(v);
        return;
      }

      const payload = buildPayload();
      setSubmitting(true);

      // If parent provided an onComplete handler (MultiStepForm), call it and let parent route
      if (typeof onComplete === "function") {
        try {
          onComplete(payload);

          // ensure state isn't left stuck if user navigates back
          setSubmitting(false);
          return;
        } catch (err) {
          setError(err.message || "Submission handler failed");
          setSubmitting(false);
          return;
        }
      }

      // Standalone behavior: POST to Netlify function and redirect to policy
      try {
        const res = await fetch(submitUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
        const text = await res.text();
        let json;
        try {
          json = JSON.parse(text);
        } catch {
          json = { raw: text };
        }

        if (!res.ok) {
          setError(json && json.error ? json.error : `Submit failed: ${res.status}`);
          setSubmitting(false);
          return;
        }

        // success
        window.location.href = redirectTo;
      } catch (err) {
        setError(err.message || "Network error");
        setSubmitting(false);
      }
    };

    return (
      <form className="adult-form" onSubmit={handleSubmit} noValidate>
        {/* Honeypot: hidden field to catch bots (should stay empty) */}
        <div style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
          <label>
            Website
            <input
              type="text"
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              autoComplete="off"
              tabIndex={-1}
            />
          </label>
        </div>

        <h2>Young Adult Registration (18–23)</h2>
        <p className="muted small">Please fill out the information below. Fields marked with * are required.</p>

        {/* Personal info -> two-column layout on wider screens */}
        <div className="two-col">
          <div className="field">
            <label>First name <span className="required">*</span></label>
            <input
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              required
              aria-required="true"
            />
          </div>
          <div className="field">
            <label>Last name <span className="required">*</span></label>
            <input
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              required
              aria-required="true"
            />
          </div>
        </div>

        <div className="two-col">
          <div className="field">
            <label>Date of Birth <span className="required">*</span></label>
            <input
              type="text"
              value={dob}
              onChange={handleDobChange}
              required
              aria-required="true"
              placeholder="MM/DD/YYYY"
              inputMode="numeric"
              maxLength={10}
            />
          </div>
          <div className="field">
            <label>Phone <span className="required">*</span></label>
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              required
              aria-required="true"
              placeholder="e.g. 555-555-5555"
            />
          </div>
        </div>

        <div className="field">
          <label>Email <span className="required">*</span></label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            aria-required="true"
            placeholder="you@example.com"
          />
        </div>

        <section className="field">
          <label className="field-label">Where will you be staying? <span className="required">*</span></label>
          <div style={{ border: "1px solid var(--input-border)", padding: 8, borderRadius: 6 }}>
            <label style={{ display: "inline-block", marginRight: 12 }}>
              <input
                type="radio"
                name="stay"
                value="girls-cabin"
                checked={stayOption === "girls-cabin"}
                onChange={() => setStayOption("girls-cabin")}
                required
              /> Girls cabin
            </label>
            <label style={{ display: "inline-block", marginRight: 12 }}>
              <input
                type="radio"
                name="stay"
                value="boys-cabin"
                checked={stayOption === "boys-cabin"}
                onChange={() => setStayOption("boys-cabin")}
                required
              /> Boys cabin
            </label>
            <label style={{ display: "inline-block", marginRight: 12 }}>
              <input
                type="radio"
                name="stay"
                value="dorms"
                checked={stayOption === "dorms"}
                onChange={() => setStayOption("dorms")}
                required
              /> Dorm/Team Lead Staying in Dorms
            </label>
            <label style={{ display: "inline-block", marginRight: 12 }}>
              <input
                type="radio"
                name="stay"
                value="married"
                checked={stayOption === "married"}
                onChange={() => setStayOption("married")}
                required
              /> Married (staying with spouse)
            </label>
          </div>
        </section>

        <div className="field" style={{ marginTop: 8 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={handicapAccessible}
              onChange={e => setHandicapAccessible(e.target.checked)}
            /> Do you require a handicap accessible room?
          </label>
        </div>

        {stayOption === "dorms" && (
          <div className="field">
            <label>Which dorm? <span className="required">*</span></label>
            <div>
              <label style={{ display: "inline-block", marginRight: 12 }}>
                <input
                  type="radio"
                  name="dormChoice"
                  value="Girls dorm"
                  checked={dormChoice === "Girls dorm"}
                  onChange={() => setDormChoice("Girls dorm")}
                  required
                /> Girls dorm
              </label>
              <label style={{ display: "inline-block", marginRight: 12 }}>
                <input
                  type="radio"
                  name="dormChoice"
                  value="Boys dorm"
                  checked={dormChoice === "Boys dorm"}
                  onChange={() => setDormChoice("Boys dorm")}
                  required
                /> Boys dorm
              </label>
            </div>
          </div>
        )}

        {stayOption === "married" && (
          <div className="field">
            <label>Spouse name <span className="required">*</span></label>
            <input
              value={spouseName}
              onChange={e => setSpouseName(e.target.value)}
              required
              aria-required="true"
            />
          </div>
        )}

        <h3>Emergency Contact 1</h3>
        <div className="two-col">
          <div className="field">
            <label>Name <span className="required">*</span></label>
            <input
              value={emergency1Name}
              onChange={e => setEmergency1Name(e.target.value)}
              required
              placeholder="First & Last Name"
              aria-required="true"
            />
          </div>
          <div className="field">
            <label>Phone <span className="required">*</span></label>
            <input
              value={emergency1Phone}
              onChange={e => setEmergency1Phone(e.target.value)}
              required
              placeholder="e.g. 555-555-5555"
              aria-required="true"
            />
          </div>
        </div>

        <h3>Emergency Contact 2</h3>
        <div className="two-col">
          <div className="field">
            <label>Name</label>
            <input
              value={emergency2Name}
              onChange={e => setEmergency2Name(e.target.value)}
              placeholder="First & Last Name"
            />
          </div>
          <div className="field">
            <label>Phone</label>
            <input
              value={emergency2Phone}
              onChange={e => setEmergency2Phone(e.target.value)}
              placeholder="e.g. 555-555-5555"
            />
          </div>
        </div>

        {/* --- Allergies dropdown + conditional details + policy acknowledgement --- */}
        <div className="field">
          <label>Do you have allergies? <span className="required">*</span></label>
          <select
            value={hasAllergies}
            onChange={(e) => {
              const v = e.target.value;
              setHasAllergies(v);

              // Clear dependent fields when switching to "no"
              if (v !== "yes") {
                setAllergies("");
                setAllergyAcknowledged(false);
              }
            }}
            required
            aria-required="true"
          >
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>

        {hasAllergies === "yes" && (
          <div className="field allergy-block">
            <label>What are your allergies? <span className="required">*</span></label>
            <input
              value={allergies}
              onChange={e => setAllergies(e.target.value)}
              required
              aria-required="true"
              placeholder="e.g. Peanuts, Penicillin"
            />
            <div className="rooming-note" style={{ marginTop: 8 }}>
              <p className="muted" style={{ margin: 0 }}>
                Please note: we cannot guarantee there will not be cross contamination of food allergens.
              </p>
              <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={allergyAcknowledged}
                  onChange={e => setAllergyAcknowledged(e.target.checked)}
                />{" "}
                I acknowledge and agree to the allergy policy above.
              </label>
            </div>
          </div>
        )}

        {error && <div className="note" style={{ color: "crimson" }}>{error}</div>}

        <div className="form-footer">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Submitting…" : "Next"}
          </button>
        </div>
      </form>
    );
  }